import { Field, Formik } from 'formik';
import { AnimatePresence, motion } from 'framer-motion';
import KTFormGroup from 'general/components/OtherKeenComponents/Forms/KTFormGroup';
import KTFormInput, {
  KTFormInputType,
} from 'general/components/OtherKeenComponents/Forms/KTFormInput';
import KTCheckbox, {
  KTCheckboxStyle,
  KTCheckboxType,
} from 'general/components/OtherKeenComponents/KTFormControls/KTCheckbox';
import KTRadio, {
  KTRadioType,
} from 'general/components/OtherKeenComponents/KTFormControls/KTRadio';
import GoogleRecaptchaForm from 'general/components/Others/GoogleRecaptchaForm';
import AppColors from 'general/constants/AppColors';
import AppConfigs from 'general/constants/AppConfigs';
import AppConstants from 'general/constants/AppConstants';
import AppRoute from 'general/constants/AppRoute';
import ToastHelper from 'general/helpers/ToastHelper';
import UIHelper from 'general/helpers/UIHelper';
import Utils from 'general/utils/Utils';
import useAppConfig from 'hooks/useAppConfig';
import _ from 'lodash';
import PropTypes from 'prop-types';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useSearchParams } from 'react-router-dom';
import * as Yup from 'yup';

SignUpForm.propTypes = {
  role: PropTypes.string,
  onSubmit: PropTypes.func,
  onCancel: PropTypes.func,
  isSaleOnly: PropTypes.bool,
};

SignUpForm.defaultProps = {
  role: '',
  onSubmit: null,
  onCancel: null,
  isSaleOnly: false,
};

/**
 *
 * @param {{
 * role: string,
 * onSubmit: function,
 * onCancel: function,
 * isSaleOnly: boolean,
 * }} props
 * @returns
 */
function SignUpForm(props) {
  // MARK: --- Params ---
  const { t } = useTranslation();
  const { role, onSubmit, onCancel, isSaleOnly } = props;
  const [isCheckCaptcha, setCheckCaptcha] = useState(false);
  const [isRoleSale, setRoleSale] = useState(false);
  const [searchParams] = useSearchParams();
  const referralCode = searchParams.get('referralCode');
  const [isAgencyIndividual, setAgencyIndividual] = useState(true);
  const termsUrl = useAppConfig()?.termsUrl;

  useEffect(() => {
    setRoleSale(role === 'SALE');
  }, [role]);

  // MARK: --- Functions ---
  function handleSubmit(values) {
    const copiedValues = {
      ...values,
      primaryRole: values.requestedCommissionModes,
      requestedCommissionModes: values.requestedCommissionModes
        ? [values.requestedCommissionModes]
        : [],
    };
    delete copiedValues['acceptTerms'];
    delete copiedValues['confirmPassword'];
    if (onSubmit) {
      onSubmit(Utils.transformEmptyStrings(copiedValues));
    }
  }

  function handleCancel() {
    if (onCancel) {
      onCancel();
    }
  }

  return (
    <AnimatePresence mode="wait">
      <motion.div
        className="login-form"
        initial={{ y: 10, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        exit={{ y: -10, opacity: 0 }}
        transition={{ duration: 0.2 }}
      >
        {/* form */}
        <Formik
          initialValues={{
            operatorId: 1,
            phone: '',
            type: AppConstants.accountType.individual,
            email: '',
            fullname: '',
            name: '',
            password: '',
            confirmPassword: '',
            acceptTerms: true,
            requestedCommissionModes: 'GROUP',
            referralCode: referralCode ?? '',
            taxCode: '',
          }}
          validationSchema={Yup.object({
            fullname: Yup.string()
              .required('Bạn chưa nhập tên')
              .test('len', t('invalid', { field: 'fullname' }), (val) => {
                if (val) {
                  return val.toString().trim();
                }
              }),
            email: Yup.string()
              .required(t('emailRequired'))
              .test('len', t('Email không hợp lệ', { field: 'email' }), (val) => {
                if (val) {
                  return (
                    !val.toString().includes(' ') && val.toString().match(AppConfigs.regex.email)
                  );
                }
                return false;
              }),
            phone: Yup.string()
              .required('Bạn chưa nhập số điện thoại')
              .test('len', t('invalid', { field: 'phone' }), (val) => {
                if (val) {
                  return (
                    !val.toString().includes(' ') && val.toString().match(AppConfigs.regex.phone)
                  );
                }
              }),
            taxCode: Yup.string().when('type', {
              is: (type) => type === 'ORGANIZATION',
              then: (schema) =>
                schema
                  .required(t('required'))
                  .test('len', t('Mã số thuế không hợp lệ', { field: 'taxCode' }), (val) => {
                    if (val) {
                      return (
                        !val.toString().includes(' ') &&
                        (val.toString().match(AppConfigs.regex.taxCode10) ||
                          val.toString().match(AppConfigs.regex.taxCode13))
                      );
                    }
                  }),
              otherwise: (schema) => schema.notRequired(),
            }),
            password: Yup.string()
              .required('Bạn chưa nhập mật khẩu')
              .min(6, 'Mật khẩu phải có ít nhất 6 ký tự'),
            confirmPassword: Yup.string()
              .required('Bạn chưa nhập mật khẩu')
              .oneOf([Yup.ref('newPassword')], 'Mật khẩu không khớp')
              .min(6, 'Mật khẩu phải có ít nhất 6 ký tự'),
            acceptTerms: Yup.boolean().test(
              'isTrue',
              'Vui lòng đồng ý với điều khoản trước khi tiếp tục.',
              (value) => value === true
            ),
          })}
          onSubmit={(values) => {
            if (!isCheckCaptcha) {
              ToastHelper.showError('Vui lòng xác thực mã captcha');
            } else if (values.requestedCommissionModes.length < 1) {
              ToastHelper.showError('Cần chọn ít nhất một vai trò cho tài khoản');
            } else {
              handleSubmit(values);
            }
          }}
        >
          {(formikProps) => (
            <form
              className="form fv-plugins-bootstrap fv-plugins-framework"
              onSubmit={formikProps.handleSubmit}
              noValidate
              autoComplete="off"
            >
              <div className="row">
                {/* title */}
                <div className="pb-5 pt-lg-5 pt-8 text-center">
                  <h3
                    className="font-weight-boldest"
                    style={{
                      fontSize: Utils.returnFontSize(30),
                      color: AppColors.gray[900],
                    }}
                  >
                    {t('Đăng ký tài khoản')}
                  </h3>
                  <span
                    className="text-muted font-weight-bolder"
                    style={{
                      fontSize: Utils.returnFontSize(16),
                    }}
                  >
                    {t('inputDetailInfoToSignUp')}
                  </span>
                </div>
                {!isSaleOnly && (
                  <div className="">
                    <KTFormGroup
                      label={<span className="font-weight-bolder">{t('role')}</span>}
                      inputName="requestedCommissionModes"
                      additionalClassName="fv-plugins-icon-container mb-3"
                      additionalLabelClassName="mb-0 font-size-h6 font-weight-bolder text-gray-700 mb-2"
                      inputElement={
                        <div className="radio-inline gap-5">
                          <Field name="requestedCommissionModes">
                            {({ field, form, meta }) => {
                              const roles = ['GROUP', 'INVESTOR'];

                              return (
                                <div className={'d-flex gap-5'}>
                                  {roles.map((role, index) => (
                                    <div key={role}>
                                      <KTRadio
                                        name={`${field.name}-${role}`}
                                        checked={field.value == role}
                                        label={t(
                                          `agencyRole_${AppConstants.userAgencyType[role.toLowerCase()]
                                          }`
                                        )}
                                        type={KTRadioType.outline}
                                        additionalClassName="radio-success"
                                        onChange={(value) => {
                                          form.setFieldValue(field.name, role);
                                        }}
                                        enableCheckValid
                                        isValid={_.isEmpty(meta.error)}
                                        isTouched={meta.touched}
                                        feedbackText={meta.error}
                                        disabled={role === 'INVESTOR'}
                                      />
                                    </div>
                                  ))}
                                </div>
                              );
                            }}
                          </Field>
                        </div>
                      }
                    />
                  </div>
                )}
                <div className="">
                  <KTFormGroup
                    label={
                      <span className="font-weight-bolder">
                        {formikProps?.values?.requestedCommissionModes === 'SALE'
                          ? 'Loại đại lý'
                          : 'Loại nhà đầu tư'}
                      </span>
                    }
                    inputName="type"
                    additionalClassName="fv-plugins-icon-container mb-2"
                    additionalLabelClassName="mb-0 font-size-h6 font-weight-bolder text-gray-700 mb-2"
                    inputElement={
                      <div className="radio-inline w-100">
                        <Field name="type">
                          {({ field, form, meta }) => (
                            <div className="w-100 d-flex gap-5">
                              <div className="">
                                <KTRadio
                                  name={field.name + 'INDIVIDUAL'}
                                  checked={field.value == 'INDIVIDUAL'}
                                  label={t(`accountType_${AppConstants.accountType.individual}`)}
                                  type={KTRadioType.outline}
                                  additionalClassName="radio-success"
                                  onChange={(value) => {
                                    form.setFieldValue(field.name, 'INDIVIDUAL');
                                    setAgencyIndividual(true);
                                  }}
                                  enableCheckValid
                                  isValid={_.isEmpty(meta.error)}
                                  isTouched={meta.touched}
                                  feedbackText={meta.error}
                                />
                              </div>
                              <div className="">
                                <KTRadio
                                  name={field.name + 'ORGANIZATION'}
                                  checked={field.value == 'ORGANIZATION'}
                                  label={t(`accountType_${AppConstants.accountType.organization}`)}
                                  type={KTRadioType.outline}
                                  additionalClassName="radio-success"
                                  onChange={(value) => {
                                    form.setFieldValue(field.name, 'ORGANIZATION');
                                    setAgencyIndividual(false);
                                  }}
                                  enableCheckValid
                                  isValid={_.isEmpty(meta.error)}
                                  isTouched={meta.touched}
                                  feedbackText={meta.error}
                                />
                              </div>
                            </div>
                          )}
                        </Field>
                      </div>
                    }
                  />
                </div>
                <div className="col-12">
                  <div
                    className="mb-5"
                    style={{
                      borderBottom: `1px dashed ${AppColors.gray[300]}`,
                    }}
                  />
                </div>

                <div className={isAgencyIndividual ? 'col-6 pr-2' : 'col-12'}>
                  <KTFormGroup
                    isRequired={true}
                    label={isAgencyIndividual ? t('fullname') : t('Tên tổ chức/doanh nghiệp')}
                    inputName="fullname"
                    additionalClassName="fv-plugins-icon-container mb-4"
                    additionalLabelClassName="mb-0 font-size-h6 font-weight-bolder text-gray-700"
                    inputElement={
                      <Field name="fullname">
                        {({ field, form, meta }) => (
                          <KTFormInput
                            name={field.name}
                            value={field.value}
                            onChange={(value) => {
                              form.setFieldValue(field.name, value);
                              form.setFieldValue('name', value);
                            }}
                            onBlur={() => {
                              form.setFieldTouched(field.name, true);
                            }}
                            enableCheckValid
                            isValid={_.isEmpty(meta.error)}
                            isTouched={meta.touched}
                            feedbackText={meta.error}
                            placeholder={
                              isAgencyIndividual
                                ? t('enterFullname')
                                : t('Nhập tên tổ chức/doanh nghiệp')
                            }
                            type={KTFormInputType.text}
                            solidBackground
                          />
                        )}
                      </Field>
                    }
                  />
                </div>

                {!isAgencyIndividual && (
                  <div className="col-6 pr-2">
                    <KTFormGroup
                      isRequired={true}
                      label={t('Mã số thuế doanh nghiệp')}
                      inputName="taxCode"
                      additionalClassName="fv-plugins-icon-container mb-4"
                      additionalLabelClassName="mb-0 font-size-h6 font-weight-bolder text-gray-700"
                      inputElement={
                        <Field name="taxCode">
                          {({ field, form, meta }) => (
                            <KTFormInput
                              name={field.name}
                              value={field.value}
                              onChange={(value) => {
                                form.setFieldValue(field.name, value);
                              }}
                              onBlur={() => {
                                form.setFieldTouched(field.name, true);
                              }}
                              enableCheckValid
                              isValid={_.isEmpty(meta.error)}
                              isTouched={meta.touched}
                              feedbackText={meta.error}
                              placeholder={t('Nhập mã số thuế')}
                              type={KTFormInputType.text}
                              solidBackground
                            />
                          )}
                        </Field>
                      }
                    />
                  </div>
                )}

                <div className="col-6 pl-2">
                  <KTFormGroup
                    label={t('referralCode')}
                    inputName="referralCode"
                    additionalClassName="fv-plugins-icon-container mb-4"
                    additionalLabelClassName="mb-0 font-size-h6 font-weight-bolder text-gray-700"
                    inputElement={
                      <Field name="referralCode">
                        {({ field, form, meta }) => (
                          <KTFormInput
                            name={field.name}
                            value={field.value}
                            onChange={(value) => {
                              form.setFieldValue(field.name, value);
                            }}
                            onBlur={() => {
                              form.setFieldTouched(field.name, true);
                            }}
                            enableCheckValid
                            isValid={_.isEmpty(meta.error)}
                            isTouched={meta.touched}
                            feedbackText={meta.error}
                            placeholder={t('Nhập mã giới thiệu')}
                            type={KTFormInputType.text}
                            solidBackground
                            disabled={referralCode}
                          />
                        )}
                      </Field>
                    }
                  />
                </div>

                <div className="col-6 pr-2">
                  <KTFormGroup
                    isRequired={true}
                    label={t('email')}
                    inputName="email"
                    additionalClassName="fv-plugins-icon-container mb-4"
                    additionalLabelClassName="mb-0 font-size-h6 font-weight-bolder text-gray-700"
                    inputElement={
                      <Field name="email">
                        {({ field, form, meta }) => (
                          <KTFormInput
                            name={field.name}
                            value={field.value}
                            onChange={(value) => {
                              form.setFieldValue(field.name, value);
                            }}
                            onBlur={() => {
                              form.setFieldTouched(field.name, true);
                            }}
                            enableCheckValid
                            isValid={_.isEmpty(meta.error)}
                            isTouched={meta.touched}
                            feedbackText={meta.error}
                            placeholder="Nhập email"
                            type={KTFormInputType.email}
                            solidBackground
                          />
                        )}
                      </Field>
                    }
                  />
                </div>

                <div className="col-6 pl-2">
                  <KTFormGroup
                    isRequired={true}
                    label={t('phone')}
                    inputName="phone"
                    additionalClassName="fv-plugins-icon-container mb-4"
                    additionalLabelClassName="mb-0 font-size-h6 font-weight-bolder text-gray-700"
                    inputElement={
                      <Field name="phone">
                        {({ field, form, meta }) => (
                          <KTFormInput
                            name={field.name}
                            value={field.value}
                            onChange={(value) => {
                              form.setFieldValue(field.name, value);
                            }}
                            onBlur={() => {
                              form.setFieldTouched(field.name, true);
                            }}
                            enableCheckValid
                            isValid={_.isEmpty(meta.error)}
                            isTouched={meta.touched}
                            feedbackText={meta.error}
                            placeholder={t('enterPhoneNumber')}
                            type={KTFormInputType.text}
                            solidBackground
                          />
                        )}
                      </Field>
                    }
                  />
                </div>
                <div className="col-6 pr-2">
                  <KTFormGroup
                    isRequired={true}
                    label={t('password')}
                    inputName="password"
                    additionalClassName="fv-plugins-icon-container mb-4"
                    additionalLabelClassName="mb-0 font-size-h6 font-weight-bolder text-gray-700"
                    inputElement={
                      <Field name="password">
                        {({ field, form, meta }) => (
                          <KTFormInput
                            name={field.name}
                            value={field.value}
                            onChange={(value) => {
                              form.setFieldValue(field.name, value);
                            }}
                            onBlur={() => {
                              form.setFieldTouched(field.name, true);
                            }}
                            enableCheckValid
                            isValid={_.isEmpty(meta.error)}
                            isTouched={meta.touched}
                            feedbackText={meta.error}
                            placeholder={t('enterPassword')}
                            type={KTFormInputType.customPassword}
                            solidBackground
                          />
                        )}
                      </Field>
                    }
                  />
                </div>
                <div className="col-6 pl-2">
                  <KTFormGroup
                    isRequired={true}
                    label={t('confirmPassword')}
                    inputName="confirmPassword"
                    additionalClassName="fv-plugins-icon-container mb-4"
                    additionalLabelClassName="mb-0 font-size-h6 font-weight-bolder text-gray-700"
                    inputElement={
                      <Field name="confirmPassword">
                        {({ field, form, meta }) => (
                          <KTFormInput
                            name={field.name}
                            value={field.value}
                            onChange={(value) => {
                              form.setFieldValue(field.name, value);
                            }}
                            onBlur={() => {
                              form.setFieldTouched(field.name, true);
                            }}
                            enableCheckValid
                            isValid={_.isEmpty(meta.error)}
                            isTouched={meta.touched}
                            feedbackText={meta.error}
                            placeholder={t('confirmPassword')}
                            type={KTFormInputType.customPassword}
                            solidBackground
                          />
                        )}
                      </Field>
                    }
                  />
                </div>
                <div className="col-12">
                  <div
                    className="mb-5"
                    style={{
                      borderBottom: `1px dashed ${AppColors.gray[300]}`,
                    }}
                  />
                </div>
              </div>
              <div className="checkbox-list form-group mb-5">
                <Field name="acceptTerms">
                  {({ field, form, meta }) => (
                    <KTCheckbox
                      additionalClassName="checkbox-success"
                      name={field.name}
                      checked={field.value}
                      onChange={(value) => {
                        form.setFieldValue(field.name, value);
                      }}
                      onBlur={() => {
                        form.setFieldTouched(field.name, true);
                      }}
                      enableCheckValid
                      isValid={_.isEmpty(meta.error)}
                      isTouched={meta.touched}
                      feedbackText={meta.error}
                      label={
                        <div>
                          <span
                            className="font-weight-bold mr-0"
                            style={{ color: AppColors.gray[600] }}
                          >
                            {t('iAgreeThe')}{' '}
                          </span>
                          <span
                            style={UIHelper.buildFontStyle('600', '14px', AppColors.primary)}
                            className="cursor-pointer hover-opacity-60"
                            onClick={() => {
                              Utils.openInNewTab(termsUrl);
                            }}
                          >
                            {t('termsAndConditions').toLowerCase()}
                          </span>
                        </div>
                      }
                      style={KTCheckboxStyle.rounded}
                      type={KTCheckboxType.default}
                    />
                  )}
                </Field>
              </div>

              <div className="form-group mb-2">
                <GoogleRecaptchaForm
                  onSuccess={() => {
                    setCheckCaptcha(true);
                  }}
                />
              </div>

              {/* action */}
              <div className="form-group d-flex flex-wrap pb-lg-0 mb-0">
                <button
                  type="submit"
                  className={`btn btn-success font-weight-bolder font-size-h6 px-8 py-4 my-3 flex-grow-1 d-flex align-items-center justify-content-center ${formikProps.values.acceptTerms ? '' : 'disabled'
                    }`}
                >
                  {t('signUp')}
                </button>
              </div>

              <div className="text-center pb-7">
                <span style={UIHelper.buildFontStyle('400', '14px', AppColors.gray[600])}>
                  Đã có tài khoản?
                </span>
                <Link
                  to={`${AppRoute.auth}/sign-in`}
                  className="ml-1 hover-opacity-60"
                  style={UIHelper.buildFontStyle('700', '14px', AppColors.primary)}
                >
                  Đăng nhập
                </Link>
              </div>
            </form>
          )}
        </Formik>
      </motion.div>
    </AnimatePresence>
  );
}

export default SignUpForm;
